/**
 * @file Custom hook that provides route to relevant initial page for storage flow, for a given user
 */

import { type OrderResponseWithItems, OrderState, StorageUnitOrderState } from '@wanda-space/types'
import type { OrderlineInOrderResponse } from 'api-client'
import { Routes } from 'consts'
import { useAppSelector } from 'hooks/useAppSelector'
import { useAuth } from 'hooks/useAuth'
import { hasOrderlineSquareMeterOrderline } from 'routes/SquareMeterFlow/helper'

import { hasStorageUnitOrderline } from 'routes/StorageUnitFlow/helper'
import { useFeatureFlags } from './useFeatureFlags'
import { useItems } from './useItems'
import { useM2Subscriptions } from './useM2Subscriptions'
import { useOrders } from './useOrders'
import { useStorageUnitOrders } from './useStorageUnitOrders'
import { useStorageUnitSubscriptions } from './useStorageUnitSubscriptions'

export const storageKeys: { [key: string]: string } = {
  PER_ITEM: 'PER_ITEM',
  SELECT: 'SELECT',
  M2: 'M2',
  STORAGE_UNIT: 'STORAGE_UNIT',
} as const

/** The routes that are relevant to use for storage type selection */
export const StorageFlowRoutes: { [key: keyof typeof storageKeys]: string } = {
  /** Route for "per item" storage */
  [storageKeys.PER_ITEM]: Routes.Storage,
  /** Route for selecting storage type */
  [storageKeys.SELECT]: Routes.SelectStorage,
  /** Route for "storage unit" */
  [storageKeys.STORAGE_UNIT]: Routes.StorageUnitFlow,
} as const

/** The id's (used for GTM), corresponding with the routes that are relevant to use for storage type selection */
export const StorageFlowIds: { [key: keyof typeof storageKeys]: string } = {
  /** Id for "per item" storage */
  [storageKeys.PER_ITEM]: 'storage-per-item',
  /** Id for selecting storage type */
  [storageKeys.SELECT]: 'storage-select',
  /** Route for "m2" storage */
  [storageKeys.M2]: 'storage-per-m2',
  /** Route for "storage unit" */
  [storageKeys.STORAGE_UNIT]: 'storage-unit',
} as const

// convenience method
const getFlowInfo = (key: keyof typeof storageKeys) => ({
  flowRoute: StorageFlowRoutes[key],
  flowId: StorageFlowIds[key],
})

// Use these later, when BE has decorated user accordingly
/*
const isUsingM2Storage = (user: User | null): boolean =>
  user?.features?.includes('m2-storage') || false

const isUsingPerItemStorage = (user: User | null): boolean =>
  user?.features?.includes('per-item-storage') || false
*/

export function useStorageFlowRouteSelector() {
  const { isAuthenticated, isAuthenticating } = useAuth()
  const user = useAppSelector((state) => state.user.user)

  let defaultValue: {
    flowRoute: string
    flowId: string
    meterSquareOrder?: OrderResponseWithItems
    meterSquareProduct?: OrderlineInOrderResponse['product']
    perItemStorageOrder?: OrderResponseWithItems[]
    storageUnitOrder?: any
    storageUnitProduct?: OrderlineInOrderResponse['product']
  } = {
    flowRoute: '',
    flowId: '',
  }

  const { data: featureFlags, isInitialLoading: isFeatureFlagsLoading } = useFeatureFlags()

  const { data: orders, isInitialLoading: isOrdersLoading } = useOrders()
  const { data: storageUnitOrders, isInitialLoading: isStorageUnitOrderLoading } =
    useStorageUnitOrders()

  if (orders) {
    const activeOrders = orders.filter((order) => order.state === OrderState.PAYMENT_AUTHORISED)
    const meterSquareOrder = activeOrders.find((order) => {
      return hasOrderlineSquareMeterOrderline(order.orderLines)
    })

    if (meterSquareOrder) {
      const orderline = hasOrderlineSquareMeterOrderline(meterSquareOrder.orderLines)
      if (orderline) {
        defaultValue = { ...defaultValue, meterSquareOrder, meterSquareProduct: orderline.product }
      }
    } else {
      defaultValue = { ...defaultValue, perItemStorageOrder: activeOrders }
    }
  }

  if (storageUnitOrders) {
    const activeStorageUnitOrders = storageUnitOrders?.items.filter(
      (order) => order.state === StorageUnitOrderState.PAYMENT_AUTHORISED
    )

    const storageUnitOrder = activeStorageUnitOrders.find((order) =>
      hasStorageUnitOrderline(order.orderLines)
    )

    if (storageUnitOrder) {
      const orderline = hasStorageUnitOrderline(storageUnitOrder.orderLines)
      if (orderline) {
        defaultValue = { ...defaultValue, storageUnitOrder, storageUnitProduct: orderline.product }
      }
    }
  }

  // Temporary way to figure out whether user already uses m2 ................................................\
  const productSubscriptions = useM2Subscriptions()
  const usesM2 = productSubscriptions.hasSquareMeterSubscriptions

  // ........................................................................................................./

  // Temporary way to figure out whether user already uses per item storage ..................................\
  const storageUnitSubscription = useStorageUnitSubscriptions()
  const usesStorageUnit = storageUnitSubscription.hasStorageUnitSubscriptions
  // ........................................................................................................./

  // Temporary way to figure out whether user already uses per item storage ..................................\
  const { data: existingItemsOnUser, isInitialLoading: isUserLoading } = useItems(user, {
    enabled: isAuthenticated,
  })
  // ........................................................................................................./

  if (
    isOrdersLoading ||
    isFeatureFlagsLoading ||
    productSubscriptions.isInitialLoading ||
    isStorageUnitOrderLoading ||
    isUserLoading ||
    isAuthenticating
  ) {
    return defaultValue
  }

  const usesPerItem = isAuthenticated && !usesM2 && !!existingItemsOnUser?.length

  // User is not logged in - let user decide
  if (!isAuthenticated || !user) {
    return { ...defaultValue, ...getFlowInfo(storageKeys.SELECT) }
  }

  // User is logged in - check if already using m2 storage
  // if (isUsingM2Storage(user)) return getArr(storageKeys.M2) // <- Use this approach later, when BE has decorated user accordingly
  if (usesM2 || defaultValue.meterSquareOrder) {
    const flowInfo = getFlowInfo(storageKeys.M2)
    return { ...defaultValue, ...flowInfo }
  }

  // User is logged in - check if already using storage unit
  // if (isUsingStorageUnit(user)) return getArr(storageKeys.STORAGE_UNIT) // <- Use this approach later, when BE has decorated user accordingly
  if (usesStorageUnit || defaultValue.storageUnitOrder) {
    if (featureFlags?.ENABLE_PER_ITEM_FLOW_STORAGE_UNIT_SELECTION) {
      return { ...defaultValue, ...getFlowInfo(storageKeys.SELECT) }
    }
    return { ...defaultValue, ...getFlowInfo(storageKeys.STORAGE_UNIT) }
  }

  // User is logged in - not using m2 storage already - check if user already uses per item storage
  //if (isUsingPerItemStorage(user)) return getArr(storageKeys.PER_ITEM) // <- Use this approach later, when BE has decorated user accordingly
  if (
    usesPerItem ||
    (defaultValue.perItemStorageOrder && defaultValue.perItemStorageOrder.length > 0)
  ) {
    if (featureFlags?.ENABLE_PER_ITEM_FLOW_STORAGE_UNIT_SELECTION) {
      return { ...defaultValue, ...getFlowInfo(storageKeys.SELECT) }
    }
    return { ...defaultValue, ...getFlowInfo(storageKeys.PER_ITEM) }
  }

  // User is logged in - not using m2 storage already - not using per item storage already - let user decide
  return { ...defaultValue, ...getFlowInfo(storageKeys.SELECT) }
}
